import {
  Emoji,
  EmojiPicker,
  ISelectType,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CURLY_BRACKETS_VALIDATOR } from "../../../../../../utils/Validators/regexValidators";
import { REMOVE_CURLY_BRACKETS } from "../../../../../../utils/conversions";
import MainApi from "../../../../../../services/mainApi.service";
import { TProtocolDefaultForm } from "./ProtocolDefaultSchema";
import { TextFormatActions } from "../../../../../../components/FormMessage/BodyField/BodyInput/TextFormatActions/TextFormatActions";
import { AddVariablesButton } from "../../../../../../components/FormMessage/BodyField/BodyInput/AddVariablesButton/AddVariablesButton";
import { VariablesExamples } from "../../../../../../components/FormMessage/BodyField/BodyInput/VariablesExamples/VariablesExamples";
import { listProtocolDefaultMacros } from "../../../../../../services/protocolDefault.service";

const VARIABLES_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomationMacro/";
const variablesService = new MainApi<ISelectType>(VARIABLES_ROUTE);

const TICKET_AUTO_INTERNAL_TEMPLATE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationInternalTemplateField/";
const variablesInternalTemplate = new MainApi<ISelectType>(
  TICKET_AUTO_INTERNAL_TEMPLATE
);

const translationPath = "components.bodyInput.";

interface IProps {
  automationId?: string;
  name: keyof TProtocolDefaultForm;
  error?: any;
}

export const ProtocolMessageInput = memo(({ automationId, name }: IProps) => {
  const { t } = useTranslation();

  const { setError, clearErrors, watch, setValue, getFieldState, formState } =
    useFormContext<TProtocolDefaultForm>();

  const [options, setOptions] = useState<ISelectType[]>([]);
  const [message, setMessage] = useState<string>(watch("message") || "");
  const [variables, setVariables] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const { sendRequest } = useFetch(listProtocolDefaultMacros);

  const fetchVariablesHandler = useCallback(async () => {
    const { data, success } = await sendRequest({
      TicketAutomationId: automationId ? automationId : undefined,
    });
    if (data && success) setOptions(data);
  }, [sendRequest, automationId]);

  const handleEmojiClick = useCallback(
    (emoji: Emoji) => {
      const ref = inputRef.current;
      if (ref) {
        const start = ref.selectionStart ?? 0;
        const end = ref.selectionEnd ?? 0;
        const text =
          message.slice(0, start) + emoji.native + message.slice(end);
        setMessage(text);
        setCursorPosition(start + emoji.native.length);
      }
    },
    [message]
  );

  const handleEmojiOpen = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const start = inputRef.current?.selectionStart ?? 0;
    setMessage(e.target.value);
    setCursorPosition(start);
  };

  const addVariableHandler = useCallback(
    (variable: string) => {
      const ref = inputRef.current;
      if (ref) {
        const start = ref.selectionStart ?? 0;
        const end = ref.selectionEnd ?? 0;
        const text =
          message.slice(0, start) + `{{${variable}}}` + message.slice(end);
        setMessage(text);
        setCursorPosition(start + variable.length);
      }
    },
    [message]
  );

  useEffect(() => {
    if (inputRef.current && cursorPosition !== null) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setCursorPosition(null);
    }
  }, [cursorPosition]);

  useEffect(() => {
    setValue(name, message.trim(), { shouldValidate: true, shouldDirty: true });
  }, [message, name, setValue]);

  useEffect(() => {
    const matches = message.match(CURLY_BRACKETS_VALIDATOR);
    setVariables(matches || []);
  }, [message]);

  useEffect(() => {
    const optionsNames = options.map((o) =>
      o.name.replace(REMOVE_CURLY_BRACKETS, "")
    );
    if (
      variables.length > 0 &&
      variables.some(
        (v) => !optionsNames.includes(v.replace(REMOVE_CURLY_BRACKETS, ""))
      )
    ) {
      setError(name, {
        type: "invalid_variable",
        message: translationPath + "error",
      });
    } else {
      clearErrors(name);
    }
  }, [variables, options, name, setError, clearErrors]);

  useEffect(() => {
    fetchVariablesHandler();
  }, [fetchVariablesHandler]);

  const fieldState = getFieldState(name, formState);
  const fieldError = fieldState.error;
  const errorKey =
    typeof fieldError?.message === "string"
      ? fieldError.message
      : typeof (fieldError as any)?.message?.message === "string"
      ? (fieldError as any).message.message
      : fieldError?.type;

  const textValue = watch(name) || "";

  return (
    <Grid item xs={12} sm={12} display={"flex"} alignItems={"start"}>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <TextField
          value={message}
          onChange={handleChange}
          name={name}
          error={!!fieldError}
          helperText={fieldError ? t(errorKey || "") : " "}
          inputRef={inputRef}
          fullWidth
          size="small"
          multiline
          minRows={2}
          inputProps={{ maxLength: 1024 }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ marginBottom: 2, alignSelf: "end" }}
              >
                <Typography
                  variant="body2"
                  sx={(t) => ({
                    color:
                      t.palette.grey[t.palette.mode === "light" ? 400 : 600],
                  })}
                >
                  {textValue ? textValue.toString().length : 0}/{1024}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: "flex" }}>
          <EmojiPicker
            size="small"
            onEmojiClick={handleEmojiClick}
            onEmojiOpen={handleEmojiOpen}
          />
          <TextFormatActions
            inputRef={inputRef}
            message={message}
            setMessage={setMessage}
          />
          <AddVariablesButton
            onAddVariable={addVariableHandler}
            inputRef={inputRef}
            options={options}
          />
        </Box>
      </Box>
    </Grid>
  );
});
